import React, { useMemo } from 'react'
import { Button, FocusMode, Dimmer } from '../../../../../views/components'
import { NewContactForm } from '../../../../contacts'
import MultiPartyTable from '../../MultiPartyTable/MultiPartyTable'
import styles from './PartiesTable.module.scss'
import { keys, pipe, props as _props } from 'ramda'
import { useDispatch, useSelector } from 'react-redux'
import { partyApiSelectors } from '../../../../api/party'
import { portfolioApiSelectors } from '../../../../api/portfolio'
import { portfolioEvents, portfolioSelectors } from '../../../state'
import { userApiSelectors } from '../../../../api/user'

type Party = {
  lastFetched: string
  primary: {
    partyId: string
  }
  secondary: Array<{
    partyId: string
  }>
}

interface PartiesTableProps {
  portfolioId: string
  partyType: string
  parties: Party
  redirect(redirectTo: string): void
  removePartyFromLease(partyId: string): void
  makePartyPrimary(partyId: string): void
  addPartyToLease(partyValues: any): void
  portfolioStatus: string
}

const PartiesTable = ({
  portfolioId,
  partyType,
  parties,
  redirect,
  removePartyFromLease,
  makePartyPrimary,
  addPartyToLease,
  portfolioStatus,
}: PartiesTableProps) => {
  const dispatch = useDispatch()

  const getPartyName = useSelector(partyApiSelectors.getPartyNameById)
  const getPartyEmail = useSelector(partyApiSelectors.getPartyEmailById)
  const getIsPortfolioDraft = useSelector(portfolioApiSelectors.isDraftPortfolio)

  const isReadOnly = useSelector(userApiSelectors.isReadOnlyRole)
  const isNewPartyModalOpen = useSelector(portfolioSelectors.isNewPartyModalOpen)
  const isPortfolioDraft = getIsPortfolioDraft(portfolioId)

  const onSubmit = (values: any) => {
    const personOrCompany = keys(values)[0]
    const partyValues = {
      [personOrCompany]: {
        ...values[personOrCompany],
        tags:
          partyType === 'owner'
            ? [...values[personOrCompany].tags, 'Owner']
            : [...values[personOrCompany].tags, 'Tenant'],
      },
    }

    return addPartyToLease(partyValues)
  }

  const WrappedNewContactForm = useMemo(
    () =>
      function PartiesTableContactForm() {
        return (
          <NewContactForm
            onSubmit={onSubmit}
            allowSearchable
            getButtonText={(values: any) =>
              values?.id ? `Add ${partyType} to lease` : `Create contact and add ${partyType} to lease`
            }
          />
        )
      },
    [],
  )

  const transformParty = (id: string, isPrimary: boolean) => ({
    id: id,
    name: getPartyName(id),
    email: getPartyEmail(id),
    isPrimary,
  })

  const partyDetails = pipe(_props(['primary', 'secondary']), (parties: any) => {
    const [primary, secondary] = parties
    return primary && secondary
      ? [transformParty(primary.partyId, true), ...secondary.map((s: any) => transformParty(s.partyId, false))]
      : []
  })(parties)

  return (
    <>
      <div className={styles.root}>
        <Dimmer active={isReadOnly} bgColor="white">
          <MultiPartyTable
            data={partyDetails}
            onEdit={partyId => redirect(`/contacts/${partyId}/edit`)}
            onRemove={id => removePartyFromLease(id)}
            onMakePrimary={makePartyPrimary}
            allowEdit={portfolioStatus !== 'terminated'}
            partyType={partyType}
          />
          <div className={styles.footer}>
            {isPortfolioDraft && (
              <>
                <Button
                  onClick={() =>
                    redirect(`/leases/${portfolioId}/edit/${partyType === 'owner' ? 'property' : 'applications'}`)
                  }
                >
                  Back
                </Button>
                <Button
                  onClick={() =>
                    redirect(`/leases/${portfolioId}/edit/${partyType === 'owner' ? 'applications' : 'term'}`)
                  }
                  disabled={partyDetails.length === 0}
                >
                  Next
                </Button>
              </>
            )}
          </div>
        </Dimmer>
      </div>
      {isNewPartyModalOpen && (
        <FocusMode
          isOpen={isNewPartyModalOpen}
          title={`New ${partyType}`}
          ContentComponent={WrappedNewContactForm}
          onClose={() => dispatch(portfolioEvents.togglePartyModal())}
        />
      )}
    </>
  )
}

export default PartiesTable
